import React, {useReducer, useState, useEffect, useRef} from 'react'
import gql from 'graphql-tag'
import {Mutation} from 'react-apollo'
import {Redirect} from 'react-router-dom'
import throttle from 'lodash.throttle'
import {notification} from 'antd'
import {ShoppingCartOutlined} from '@ant-design/icons'

import PageNotFound from 'shared/components/PageNotFound'
import Layout from 'components/shared/Layout'
import Query from 'components/shared/Query'
import QueryParams from 'components/shared/QueryParams'
import ShoppingCartLines from 'components/shared/ShoppingCartLines'
import OrderSteps from 'components/shared/OrderSteps'
import ReservedBookingTimer from 'components/shared/ReservedBookingTimer'
import Button from 'components/shared/Button'
import PostponedBookingAlert from 'components/shared/PostponedBookingAlert'
import StepRenderer from '../StepRenderer'
import {getOrderingSteps, recursivelySetNextStep} from '../helpers'
import {
  stateFromWebOrder,
  webOrderInputFromProductSelection,
  getRedirectStep,
} from './helpers'
import {initialPropertiesOnWebOrder, updatingPropertiesOnWebOrder} from './webOrderFragments'

const BILLING_THROTTLE_DELAY = 3000

const webOrderQuery = gql`
  query WebOrderQuery($id: ID!) {
    viewer {
      id
    }
    webOrder(id: $id) {
      ...InitialPropertiesOnWebOrder
      ...UpdatingPropertiesOnWebOrder
    }
  }
  ${initialPropertiesOnWebOrder}
  ${updatingPropertiesOnWebOrder}
`

const updateWebOrderMutation = gql`
  mutation UpdateWebOrder($input: WebOrderInput!) {
    UpdateWebOrder(input: $input) {
      webOrder {
        ...UpdatingPropertiesOnWebOrder
      }
    }
  }
  ${updatingPropertiesOnWebOrder}
`

const submitWebOrderMutation = gql`
  mutation SubmitWebOrder($input: WebOrderInput!) {
    SubmitWebOrder(input: $input) {
      order {
        id
      }
    }
  }
`

function reducer(state, action) {
  switch (action.type) {
    case 'setBundle': {
      return {
        ...state,
        bundleId: action.bundle.id,
        products: action.products,
        premiumService: !!action.bundle.premiumService,
      }
    }
    case 'removeBundle': {
      return {
        ...state,
        bundleId: null,
        // Should probably filter out relevant products only
        // but this is how it works today as well
        products: [],
        premiumService: false,
        expressDelivery: false,
      }
    }
    case 'addProduct': {
      const newState = {
        ...state,
        products: [...state.products, action.product],
      }
      if (action.product.dependentProducts) {
        action.product.dependentProducts.forEach((product) => {
          if (!newState.products.find(p => p.__typename === product.__typename)) {
            newState.products.push(product)
          }
        })
      }
      return newState
    }
    case 'removeProduct': {
      const products = state.products.filter(p => p.__typename !== action.product.__typename)
      const cartEmpty = products.length === 0
      return {
        ...state,
        products,
        premiumService: cartEmpty ? false : state.premiumService,
        expressDelivery: cartEmpty ? false : state.expressDelivery,
      }
    }
    case 'updateProduct': {
      return {
        ...state,
        products: state.products.map((product) => {
          if (product.__typename === action.product.__typename) {
            return {...product, ...action.product}
          }
          return product
        }),
      }
    }
    case 'setInformation': {
      return {
        ...state,
        ...action.information,
      }
    }
    case 'setResourcePreference': {
      return {
        ...state,
        ...action.preferences,
        reservedBooking: null,
      }
    }
    case 'setReservedBooking': {
      return {
        ...state,
        reservedBooking: action.reservedBooking,
      }
    }
    case 'setInvoiceType': {
      return {
        ...state,
        invoiceType: action.invoiceType,
      }
    }
    case 'setInvoiceRecipient': {
      return {
        ...state,
        invoiceRecipient: action.invoiceRecipient,
      }
    }
    default: {
      throw new Error(`Unknown action ${action}`)
    }
  }
}

// Top level component for the ordering process. Holds the full order state
// and what step to render. The bill will be updated on any changed to the
// order (productSelection) state. Order state is mutated with the reducer
// provided to all steps.
const ProductSelection = (props) => {
  const [bill, setBill] = useState(null)
  const [isFetchingBill, setIsFetchingBill] = useState(false)
  const [updateError, setUpdateError] = useState(null)
  const [productSelection, productSelectionReducer] = useReducer(
    reducer,
    stateFromWebOrder(props.webOrder)
  )

  const throttledUpdateBill = useRef(
    throttle((webOrderId, productSelection) => {
      setIsFetchingBill(true)
      setUpdateError(null)
      return props.updateWebOrderMutation({
        variables: {
          input: {
            id: webOrderId,
            ...webOrderInputFromProductSelection(productSelection),
          },
        }
      })
        .then(({data}) => data.UpdateWebOrder.webOrder)
        .then(({bill}) => {
          setBill(bill)
          setIsFetchingBill(false)
        })
        .catch(err => {
          notification['error']({
            message: 'Lagring av bestilling feilet',
            description: err.message || 'Kunne ikke lagre bestilling grunnet en feil.',
          })
          setUpdateError(err)
          setIsFetchingBill(false)
        })
    }, BILLING_THROTTLE_DELAY)
  )

  const submitWebOrder = () => {
    return props.submitWebOrderMutation({
      variables: {
        input: {
          id: props.webOrder.id,
          ...webOrderInputFromProductSelection(productSelection),
        },
      }
    })
      .then(({data}) => data.SubmitWebOrder.order)
      .catch(err => {
        notification['error']({
          message: 'Innsending av bestilling feilet',
          description: err.message || 'Kunne ikke sende inn bestilling grunnet en feil.',
        })
      })
  }

  const redirectStep = getRedirectStep(props.webOrder)

  useEffect(() => {
    if (redirectStep) return
    setIsFetchingBill(true)
    setUpdateError(null)
    throttledUpdateBill.current(props.webOrder.id, productSelection)
  }, [productSelection, props.webOrder.id, redirectStep])

  if (redirectStep) {
    // Steps of creating the estate is not complete. Go back to new estate
    return (
      <Redirect to={`/bestilling/ny/${props.webOrder.id}?step=${redirectStep}`} />
    )
  }

  const removeBundle = (bundle) => {
    productSelectionReducer({type: 'removeBundle', bundle})
  }

  const allSteps = getOrderingSteps({
    viewer: props.viewer,
    webOrder: props.webOrder,
    productSelection,
  })

  const validSteps = []
  // Only include steps for creating new estate if there is no existing estate
  if (!props.webOrder.estate) {
    recursivelySetNextStep(validSteps, allSteps.filter(s => s.scope === 'estate'))
  }
  recursivelySetNextStep(validSteps, allSteps.filter(s => s.scope === 'order'))

  const defaultStep = props.webOrder.segment.type === 'commercial'
    ? 'produkter'
    : 'pakker'

  return (
    <>
      <OrderSteps
        webOrder={props.webOrder}
        steps={validSteps}
        currentStep={props.queryParams.step || defaultStep}
      />

      <ReservedBookingTimer
        reservedBooking={props.webOrder.reservedBooking}
        productSelectionReducer={productSelectionReducer}
      />

      <PostponedBookingAlert webOrder={props.webOrder} />

      <StepRenderer
        scope="order"
        defaultStep={defaultStep}
        removeReservation={() => productSelectionReducer({type: 'setReservedBooking', reservedBooking: null})}
        isRemovingReservation={isFetchingBill}
        webOrder={props.webOrder}
        bill={bill}
        isFetchingBill={isFetchingBill}
        updateError={updateError}
        viewer={props.viewer}
        productSelection={productSelection}
        productSelectionReducer={productSelectionReducer}
        queryParams={props.queryParams}
        setQueryParams={props.setQueryParams}
        submitWebOrder={submitWebOrder}
        productAvailability={props.productAvailability}
      />

      {props.showCart && (
        <Layout.Drawer
          heading="Handlekurv"
          onClose={props.closeCart}
        >
          <ShoppingCartLines
            bill={bill}
            isFetchingBill={isFetchingBill}
            productSelection={productSelection}
            webOrder={props.webOrder}
            removeBundle={removeBundle}
          />
        </Layout.Drawer>
      )}
    </>
  )
}

// Root Layout component that holds the state of whether or not the
// shopping cart is expanded. Shows loading/error state for WebOrder query
// or renders ProductSelection when everything is loaded.
const LayoutComponentWithCart = (props) => {
  const [showCart, setShowCart] = useState(false)

  const openCart = () => setShowCart(true)
  const closeCart = () => setShowCart(false)

  if (!props.webOrder) {
    return (
      <Layout>
        <PageNotFound />
      </Layout>
    )
  }

  return (
    <Layout
      documentTitle="Ny bestilling"
      rightAction={
        <Button icon={<ShoppingCartOutlined />} onClick={openCart} />
      }
    >
      <ProductSelection
        {...props}
        showCart={showCart}
        closeCart={closeCart}
      />
    </Layout>
  )
}

// Collect dependencies like QueryParams, Mutations and the Query results
// of the requested WebOrder. Provide it all to LayoutComponentWithCart.
const WrappedProductSelection = (props) => (
  <QueryParams>
    {({queryParams, setQueryParams}) => (
      <Mutation mutation={updateWebOrderMutation}>
        {(updateWebOrder) => (
          <Mutation mutation={submitWebOrderMutation}>
            {(submitWebOrder) => (
              <Query
                inlineLoading
                inlineError
                query={webOrderQuery}
                variables={{id: props.match.params.webOrderId}}
              >
                {({getData}) => {
                  return (
                    <LayoutComponentWithCart
                      webOrder={getData('webOrder')}
                      viewer={getData('viewer')}
                      productAvailability={getData('webOrder.broker.productAvailability')}
                      queryParams={queryParams}
                      setQueryParams={setQueryParams}
                      updateWebOrderMutation={updateWebOrder}
                      submitWebOrderMutation={submitWebOrder}
                    />
                  )
                }}
              </Query>
            )}
          </Mutation>
        )}
      </Mutation>
    )}
  </QueryParams>
)

export default WrappedProductSelection
